// Mutual separation: the shapes the separations/* handlers return, the words for
// their codes, and the pure helpers the page uses. Every figure and every rule is
// Postgres's (db/migrations/5400 to 5406); this file only names things. Kept free
// of React and of extensionless imports so web/test can import it.

export type Basis = 'v6' | 'v5_addendum_1' | 'v5' | 'manual_review'
export type Status = 'preparing' | 'issued' | 'signing' | 'signed' | 'declined' | 'cancelled'
export type Gate = { no: number; key: string; ok: boolean; text: string; clock?: Record<string, unknown>; ready_on?: string }
export type Review = {
  step: number; title: string; status: 'pending' | 'approved' | 'returned'; assignee_id: string | null; assignee: string | null
  due_on: string | null; decided_by: string | null; decided_at: string | null; note: string | null; details: Record<string, unknown>
  figures_hash_ok: boolean
}
export type Line = {
  key: string; kind: 'earning' | 'deduction' | 'statement'; amount: number | null; period: string; basis: string; source: string
  fixed_text?: string; estimate?: boolean; mandatory?: boolean; days?: number | null
}
export type AnnexItem = { kind: string; description: string; source: string; source_ref?: string; amount: number; allowed: number; held_back: number; calculation?: string[] }
export type Figures = {
  status: 'ok' | 'blocked'; missing: string[]; info?: string[]; as_at: string; basis: Basis
  termination_date: string; last_working_day: string; period_start: string; pay_by: string
  lines: Line[]
  annexure_a: { items: AnnexItem[]; total: number; cap: number; floor: number; room: number; ranked_ahead: number; remuneration_after: number }
  excluded: { source: string; amount: number; reason: string }[]
  totals: { earnings: number; deductions: number; net_estimate: number; remuneration_in_money: number; paid_hours: number; paye_estimated: boolean }
  averages: { from: string; to: string; weeks: number; weekly: number; daily: number; gbs_per_shift: number | null; rostered_shifts: number }
  escalate: Record<string, boolean>
}
/** What separation_figures_v1 returns when it refuses before computing anything. */
export type FiguresRefusal = { status: 'blocked'; missing: string[]; as_at: string }

export type Access = { can_view: boolean; can_open: boolean; is_admin: boolean; is_approver: boolean; is_voluntariness_reviewer: boolean; is_figures_checker: boolean }
export type ListRow = {
  id: string; case_ref: string; status: Status; driver_name: string; offboarding_case_ref: string; offboarding_case_id: string
  basis: Basis; termination_date: string | null; delivered_at: string | null; my_step: number | null
  steps: { step: number; status: string; assignee: string | null; due_on: string | null }[]
}
export type List = { ok: boolean; access: Access; cases: ListRow[]; can_start: { offboarding_case_id: string; case_ref: string; driver_name: string }[] }
export type Detail = {
  ok: boolean; access: Access; me: string
  case: Record<string, any> & { id: string; case_ref: string; status: Status }
  driver_name: string
  offboarding: Record<string, any> & { id: string; case_ref: string }
  basis_live: { basis: Basis; reason: string; agreement_version: string | null; agreement_signed_on: string | null; addendum_effective_date: string | null }
  clause_map: { rule: string; applies: boolean; clause: string | null; note: string | null }[] | null
  figures: Figures | FiguresRefusal | null
  figures_computed_at: string | null
  issue_gates: Gate[] | null
  sign_gates: Gate[] | null
  suspension: { suspended: boolean; suspended_on?: string; hearing_by?: string; hearing_date?: string | null; working_days_left?: number }
  inspection: { id: string; inspected_at: string; odometer: number | null; fuel: string | null; condition: string | null; registration: string | null; attached: boolean | null; in_window: boolean } | null
  leave: { days: number | null; for_date: string | null; read_at: string | null; by: string | null; current: boolean } | null
  pay_periods_to_check: { from: string | null; to: string | null } | null
  ui19_suggestion: { code: 6 | 16 | null; certain: boolean; reason: string } | null
  reviews: Review[]
  arrears_items: { id: string; pay_period: string; component: string; amount_zar: number; source: string; recorded_by: string; withdrawn: boolean }[]
  people: Record<string, string | null>
  conduct_options: { id: string; label: string }[]
  incident_options: { id: string; label: string }[]
  document: Record<string, any> | null
  events: { at: string; kind: string; by: string | null; detail: Record<string, unknown> }[] | null
}

export const STATUS_LABEL: Record<Status, string> = {
  preparing: 'Preparing', issued: 'Issued, cooling off', signing: 'Out for signature',
  signed: 'Signed', declined: 'Declined by the driver', cancelled: 'Withdrawn',
}
export const BASIS_LABEL: Record<Basis, string> = {
  v6: 'v6 agreement', v5_addendum_1: 'v5 with Addendum No. 1', v5: 'v5 agreement', manual_review: 'Manual review',
}
export const LINE_LABEL: Record<string, string> = {
  gbs_current: 'Guaranteed Base Salary, current period',
  arrears: 'Arrears from earlier periods (never waived)',
  revenue_share_current: 'Revenue Share, current period',
  revenue_share_arrears: 'Revenue Share outstanding, earlier periods',
  unworked_rostered_shifts: 'Rostered shifts not worked, paid as if worked',
  leave_pay: 'Accrued annual leave',
  notice: 'Notice',
  other_agreed: 'Other agreed payment',
  uif: 'UIF, estimate',
  paye: 'PAYE, estimate',
  medical: 'Medical contribution',
  yes_offset: 'YES Salary set off',
  yes_statement: 'YES stipend',
}

// The facts step 1 records, grouped the way the conversation happens, with how
// each is edited. Every key here is one separation_update_v1 accepts;
// web/test/separation.test.ts compares the two.
export type FieldKind = 'text' | 'long' | 'date' | 'bool' | 'int' | 'money' | 'choice'
export type GroupKey = 'allegation' | 'who' | 'conversation' | 'suspension' | 'terms' | 'addresses' | 'escalation'
export const STEP1_GROUPS: { key: GroupKey; title: string; intro: string }[] = [
  { key: 'allegation', title: 'The allegation and the right to a hearing', intro: 'What he is alleged to have done, when it was put to him in writing, and when he was told in writing that he may have a fair hearing instead.' },
  { key: 'who', title: 'Who raised separation', intro: 'Who first suggested leaving by agreement, when, and who was there. If Fleetly suggested it in the same meeting where the allegation was first put to him, the platform cannot issue an agreement: it would not be a free choice.' },
  { key: 'conversation', title: 'The conversation', intro: 'What was said, the language it was explained in, and whether a fellow employee or trade union representative was offered.' },
  { key: 'suspension', title: 'Suspension and hearing', intro: 'Leave the suspension date blank if he is not suspended. A suspended driver\'s hearing must still be set within ten working days.' },
  { key: 'terms', title: 'The terms', intro: 'The Termination Date, whether he works until then, and anything agreed beyond what the law requires.' },
  { key: 'addresses', title: 'Addresses for notices', intro: 'Where notices about the agreement go, including after he leaves. The agreement prints these.' },
  { key: 'escalation', title: 'Anything Luyanda must see first', intro: 'A disputed figure, or an accident with injury or a third party claim, goes to Luyanda before step 4.' },
]
export const STEP1_FIELDS: { key: string; label: string; kind: FieldKind; group: GroupKey; choices?: { value: string; label: string }[]; hint?: string }[] = [
  { key: 'allegation', group: 'allegation', label: 'The allegation, as put to him in writing', kind: 'long' },
  { key: 'allegation_in_writing_on', group: 'allegation', label: 'Date the allegation was given to him in writing', kind: 'date' },
  { key: 'allegation_first_put_on', group: 'allegation', label: 'Date the allegation was first put to him', kind: 'date' },
  { key: 'hearing_right_notice_on', group: 'allegation', label: 'Date he was told in writing he may have a fair hearing', kind: 'date' },
  { key: 'hearing_right_notice_method', group: 'allegation', label: 'How he was told', kind: 'text', hint: 'For example: letter handed over, copy sent on WhatsApp' },
  { key: 'raised_by', group: 'who', label: 'Who first suggested leaving by agreement?', kind: 'choice', choices: [{ value: 'employee', label: 'The driver' }, { value: 'fleetly', label: 'Fleetly' }] },
  { key: 'first_conversation_on', group: 'who', label: 'When was it first suggested?', kind: 'date' },
  { key: 'first_conversation_present', group: 'who', label: 'Who was there?', kind: 'text', hint: 'Names, for example: Thabang Mamabolo and the driver' },
  { key: 'raised_in_allegation_meeting', group: 'who', label: 'Was that in the same meeting where the allegation was first put to him?', kind: 'bool' },
  { key: 'requested_on', group: 'who', label: 'Date the separation was requested', kind: 'date' },
  { key: 'conversation_note', group: 'conversation', label: 'What was said', kind: 'long' },
  { key: 'language', group: 'conversation', label: 'Language the agreement is explained in', kind: 'text' },
  { key: 'interpreter', group: 'conversation', label: 'Interpreter, if any', kind: 'text' },
  { key: 'representation_offered', group: 'conversation', label: 'Offered a fellow employee or trade union representative?', kind: 'bool' },
  { key: 'representative', group: 'conversation', label: 'Representative at signing, if any', kind: 'text' },
  { key: 'suspended_on', group: 'suspension', label: 'Suspended on', kind: 'date' },
  { key: 'hearing_date', group: 'suspension', label: 'Hearing date set for', kind: 'date' },
  { key: 'termination_date', group: 'terms', label: 'Termination Date', kind: 'date', hint: 'Not before the day he can sign (two working days after he receives the agreement). Employment cannot end before the agreement that ends it, and a suspension is on full pay until then.' },
  { key: 'required_to_work', group: 'terms', label: 'Does he work until the Termination Date?', kind: 'bool' },
  { key: 'unworked_shifts_override', group: 'terms', label: 'Rostered shifts not worked (only if the figures say the roster has no seat)', kind: 'int' },
  { key: 'unworked_shifts_source', group: 'terms', label: 'Where that number comes from', kind: 'text' },
  { key: 'other_agreed_payment_zar', group: 'terms', label: 'Other agreed payment (R), if any', kind: 'money', hint: 'Escalates to Luyanda; above the Delegation of Authority it goes to the board' },
  { key: 'other_agreed_payment_reason', group: 'terms', label: 'Why', kind: 'text' },
  { key: 'ownership_participating', group: 'terms', label: 'On the ownership pathway?', kind: 'bool' },
  { key: 'ownership_terms', group: 'terms', label: 'How the ownership pathway is dealt with', kind: 'text' },
  { key: 'reference_kind', group: 'terms', label: 'Reference', kind: 'choice', choices: [{ value: 'dates_only', label: 'Dates and position only' }, { value: 'agreed_letter', label: 'An agreed letter' }] },
  { key: 'property_return_by', group: 'terms', label: 'Property returned by', kind: 'date' },
  { key: 'property_return_at', group: 'terms', label: 'Returned at', kind: 'text' },
  { key: 'notice_whatsapp', group: 'addresses', label: 'WhatsApp', kind: 'text' },
  { key: 'notice_email', group: 'addresses', label: 'Email', kind: 'text' },
  { key: 'notice_address', group: 'addresses', label: 'Physical address', kind: 'text' },
  { key: 'driver_disputes_figures', group: 'escalation', label: 'Does he dispute any figure?', kind: 'bool' },
  { key: 'dispute_note', group: 'escalation', label: 'Which figure and why', kind: 'text' },
  { key: 'injury_or_third_party', group: 'escalation', label: 'Accident with injury or a third party claim?', kind: 'bool' },
]

/** Where on the page each gate is fixed, so "What is left" can take you there. */
export const GATE_SECTION: Record<string, string> = {
  basis: 'sec-basis',
  allegation_and_hearing_right: 'sec-allegation',
  who_raised: 'sec-who',
  suspension_clock: 'sec-suspension',
  exit_inspection: 'sec-inspection',
  cash: 'sec-cash',
  annexure_sources: 'sec-figures',
  arrears: 'sec-arrears',
  language: 'sec-conversation',
  ui19: 'sec-ui19',
  termination_date: 'sec-terms',
  figures: 'sec-figures',
  step1: 'sec-steps',
}

/** Fleetly suggesting it in the allegation meeting is refused; say so as the answer is given. */
export function raisedInAllegationMeeting(f: Record<string, string>): boolean {
  return f.raised_by === 'fleetly' && f.raised_in_allegation_meeting === 'yes'
}

/** The first sentence of a refusal, for a toast; the full list is on the page. */
export function firstSentence(msg: string): string {
  const m = msg.match(/^(.*?[.:])(\s|$)/)
  return m ? m[1].replace(/:$/, '.') : msg
}

export function formatZar(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === '') return 'Not known'
  const n = Number(v)
  if (!Number.isFinite(n)) return 'Not known'
  return (n < 0 ? '-R' : 'R') + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function formatDay(iso: string | null | undefined): string {
  if (!iso) return 'not recorded'
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso)
  if (Number.isNaN(d.getTime())) return 'not recorded'
  return d.toLocaleDateString('en-ZA', { timeZone: 'Africa/Johannesburg', day: 'numeric', month: 'short', year: 'numeric' })
}

export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return 'not recorded'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return 'not recorded'
  return d.toLocaleString('en-ZA', { timeZone: 'Africa/Johannesburg', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

/** A line's amount as the page shows it: a missing figure is never shown as zero. */
export function lineAmount(l: Pick<Line, 'amount' | 'fixed_text' | 'key'>): string {
  if (l.key === 'notice') return 'R0.00, waived'
  if (l.amount === null || l.amount === undefined) return l.fixed_text ? 'Not part of this payment' : 'Not known'
  return formatZar(l.amount)
}

/**
 * A refusal from separation_figures_v1 (manual review, no Termination Date, no
 * last working day) carries only status, missing and as_at, with no lines. On 7
 * October 2026 a page that assumed lines went white for MS-0001. Every reader of
 * figures checks this first.
 */
export function fullFigures(f: Figures | FiguresRefusal | null | undefined): f is Figures {
  if (!f) return false
  const g = f as Partial<Figures>
  return Array.isArray(g.lines) && !!g.totals && !!g.annexure_a && Array.isArray(g.annexure_a.items)
    && !!g.averages && Array.isArray(g.excluded)
}

export function failing(gates: Gate[] | null | undefined): Gate[] {
  return (gates ?? []).filter((g) => !g.ok)
}

/** Turns the form's strings into the patch the database expects, only for what changed. */
export function patchFrom(initial: Record<string, string>, form: Record<string, string>): Record<string, unknown> {
  const patch: Record<string, unknown> = {}
  for (const f of STEP1_FIELDS) {
    if (form[f.key] === initial[f.key]) continue
    const v = form[f.key]
    if (f.kind === 'bool') patch[f.key] = v === '' ? null : v === 'yes'
    else if (f.kind === 'int' || f.kind === 'money') patch[f.key] = v === '' ? null : Number(v)
    else patch[f.key] = v === '' ? null : v
  }
  return patch
}

export function formFrom(c: Record<string, any>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const f of STEP1_FIELDS) {
    const v = c[f.key]
    out[f.key] = v === null || v === undefined ? '' : f.kind === 'bool' ? (v ? 'yes' : 'no') : String(v)
  }
  return out
}

/** One sentence: what has to happen next, and who does it. */
/**
 * The one thing to do next, said to the person looking at the page. On 12 Oct
 * 2026 MS-0001 stalled with step 1 approved and every gate passing: issuing and
 * recording the hand-over were separate buttons lower down, nobody was told it
 * was their turn, and steps 2 to 4 never reached Yolisa, Meseum or Luyanda.
 */
export type Turn = {
  mine: boolean
  action: 'fix' | 'ui19' | 'complete_step1' | 'issue' | 'step' | 'wait' | 'done'
  step?: number
  title: string
  detail: string
  goto?: string
}
export function yourTurn(d: Pick<Detail, 'case' | 'reviews' | 'issue_gates' | 'sign_gates' | 'me' | 'access' | 'driver_name'>): Turn {
  const c = d.case
  const r = (n: number) => d.reviews.find((x) => x.step === n)
  const who = (n: number) => r(n)?.assignee ?? `the step ${n} owner`
  const owner1 = r(1)?.assignee_id === d.me || d.access.is_admin
  const driver = d.driver_name || 'the driver'
  if (c.status === 'signed') return { mine: false, action: 'done', title: 'Closed out', detail: 'Signed by both. Final pay is written from the signed figures and filed to SimplePay with the UI-19 reason; the conduct case is closed with no finding.' }
  if (c.status === 'declined') return { mine: false, action: 'done', title: 'The driver declined', detail: 'The hearing route continues as before. Cancel the offboarding case if it was opened only for this.' }
  if (c.status === 'cancelled') return { mine: false, action: 'done', title: 'Withdrawn', detail: 'Nothing more happens on this separation.' }
  if (c.status === 'signing') return { mine: false, action: 'wait', title: 'Out for signature', detail: `SignNow has it: ${driver} signs first, then Luyanda. The platform checks every fifteen minutes and files everything once both have signed.` }

  if (c.status === 'preparing') {
    const bad = failing(d.issue_gates).filter((g) => g.key !== 'step1')
    const ui19 = bad.find((g) => g.key === 'ui19')
    if (ui19 && !c.ui19_code && d.access.is_approver) {
      return { mine: true, action: 'ui19', title: 'Your turn: decide the UI-19 reason', detail: ui19.text, goto: 'sec-ui19' }
    }
    if (bad.length) {
      const rest = bad.filter((g) => !(g.key === 'ui19' && !c.ui19_code))
      if (owner1 && rest.length) {
        return { mine: true, action: 'fix', title: `Your turn: ${rest.length} ${rest.length === 1 ? 'thing' : 'things'} to settle, then complete step 1`,
          detail: `First: ${rest[0].text} The full list is just below, each with "Go there".`, goto: GATE_SECTION[rest[0].key] ?? 'sec-step1' }
      }
      if (ui19 && !c.ui19_code) return { mine: false, action: 'wait', title: 'Waiting for Luyanda', detail: 'Luyanda decides the UI-19 reason before the agreement can be issued.' }
      return { mine: false, action: 'wait', title: `Waiting for ${who(1)}`, detail: `${who(1)} is finishing step 1: ${bad[0].text}` }
    }
    if (r(1)?.status !== 'approved') {
      return owner1
        ? { mine: true, action: 'complete_step1', title: 'Your turn: complete step 1', detail: `Every check passes. Completing step 1 issues the agreement, with the figures frozen as they are now, and sends steps 2 and 3 to ${who(2)} and ${who(3)}.` }
        : { mine: false, action: 'wait', title: `Waiting for ${who(1)}`, detail: `Every check passes. ${who(1)} completes step 1, which issues the agreement.` }
    }
    return owner1
      ? { mine: true, action: 'issue', title: 'Your turn: issue the agreement', detail: `Step 1 is complete and every check passes. Issuing sends steps 2 and 3 to ${who(2)} and ${who(3)}.` }
      : { mine: false, action: 'wait', title: `Waiting for ${who(1)}`, detail: `${who(1)} issues the agreement.` }
  }

  // Issued: steps 2 and 3 open at once; no hand-over stands between (5418).
  const open23 = [2, 3].map((n) => r(n)!).filter((x) => x && x.status !== 'approved')
  const mine23 = open23.find((x) => x.assignee_id === d.me)
  if (mine23) {
    return { mine: true, action: 'step', step: mine23.step, title: `Your turn: step ${mine23.step}, ${mine23.title.toLowerCase()}`,
      detail: mine23.step === 2
        ? 'Speak to the driver alone for at least five minutes about whether he wants this, then record it in your step below and approve, or return it with what has to change.'
        : 'Check every line against payroll and SimplePay, tick the three boxes in your step below and approve, or return it with what has to change.',
      goto: `sec-step-${mine23.step}` }
  }
  if (open23.length) return { mine: false, action: 'wait', title: `Waiting for ${open23.map((x) => x.assignee ?? `step ${x.step}`).join(' and ')}`, detail: `Step${open23.length > 1 ? 's' : ''} ${open23.map((x) => x.step).join(' and ')} ${open23.length > 1 ? 'are' : 'is'} open. Step 4 follows.` }
  if (r(4)?.status !== 'approved') {
    return r(4)?.assignee_id === d.me
      ? { mine: true, action: 'step', step: 4, title: 'Your turn: step 4, approve and sign', detail: 'Read the review summary, then approve. Approving sends the agreement to the driver through SignNow at once: he signs, then you.', goto: 'sec-step-4' }
      : { mine: false, action: 'wait', title: `Waiting for ${who(4)}`, detail: `${who(4)} approves step 4, which sends it to ${driver} through SignNow.` }
  }
  const f = failing(d.sign_gates)
  if (f.length) return { mine: false, action: 'wait', title: 'Sends itself for signature', detail: `Every step is approved. It goes to SignNow by itself when this clears: ${f[0].text}` }
  return { mine: false, action: 'wait', title: 'Sending for signature', detail: 'Every step is approved and every check passes. It goes to SignNow within fifteen minutes.' }
}
